import {
  parseGestorCentralizadoTotal,
  resolveEstadoLiquidacion,
  resolveIdLiquidacionCab,
} from "./gestor-centralizado.contract";

function isPlainObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function normalizeNumber(value) {
  if (value === null || value === undefined || value === "") return 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function normalizeText(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

function normalizeBoolean(value) {
  if (typeof value === "boolean") return value;
  if (value === 1 || value === "1") return true;

  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  return ["true", "s", "si", "y", "yes"].includes(normalized);
}

function pickMotivoPenalidad(row = {}) {
  return normalizeText(
    row.motivoPenalidad ??
      row.penalidadMotivo ??
      row.motivo_penalidad ??
      row.motivoPenalidades ??
      "",
  );
}

function resolveParentSequenceId(row = {}) {
  return normalizeText(row.detSequenceId ?? row.nroOs ?? row.numeroOs);
}

function resolveAdicionalParentId(row = {}) {
  return normalizeText(
    row.detSequenceId ??
      row.detSequenceIdPadre ??
      row.nroOsPadre ??
      row.osPadre ??
      row.nroOs ??
      row.numeroOs,
  );
}

function extractNestedAdicionales(row = {}) {
  if (Array.isArray(row.detalles) && row.detalles.length) return row.detalles;
  if (Array.isArray(row.adicionales) && row.adicionales.length)
    return row.adicionales;
  if (
    Array.isArray(row.serviciosAdicionales) &&
    row.serviciosAdicionales.length
  ) {
    return row.serviciosAdicionales;
  }
  return [];
}

function resolveIgv(row, totalSinIgv, totalConIgv) {
  const explicit = normalizeNumber(row.igv);
  if (explicit > 0) return explicit;

  const diff = Number((totalConIgv - totalSinIgv).toFixed(2));
  return diff > 0 ? diff : 0;
}

export function getEmptyGestorCentralizadoResumen() {
  return mapGestorCentralizadoResumen({});
}

export function mapGestorCentralizadoRow(row) {
  if (!isPlainObject(row)) return row;

  const totalFacturarSinIgv = normalizeNumber(
    row.totalFacturarSinIgv ?? row.totFacturarSinIgv,
  );
  const totalFacturarConIgv = normalizeNumber(
    row.totalFacturarConIgv ?? row.totFacturarConIgv,
  );

  return {
    id: row.id ?? row.idLiquidacionOs ?? null,
    idLiquidacionOs: row.idLiquidacionOs ?? null,
    idLiquidacionCab: row.idLiquidacionCab ?? null,
    detSequenceId: normalizeText(row.detSequenceId ?? row.nroOs),
    codTienda: normalizeText(row.codTienda ?? row.codigoTienda),
    nombreTienda: normalizeText(row.nombreTienda),
    nroOs: normalizeText(row.nroOs ?? row.detSequenceId),
    cliente: normalizeText(row.cliente),
    usuarioProveedor: normalizeText(row.usuarioProveedor),  // ← agregar
    skuServicio: normalizeText(row.skuServicio),
    servicio: normalizeText(row.servicio ?? row.descripcionServicio),
    skuProducto: normalizeText(row.skuProducto),
    producto: normalizeText(row.producto ?? row.descripcionProducto),
    cantidad: normalizeNumber(row.cantidad),
    costoTotalSinIgv: normalizeNumber(row.costoTotalSinIgv),
    costoTotalConIgv: normalizeNumber(row.costoTotalConIgv),
    penalidad: normalizeNumber(row.penalidad),
    motivoPenalidad: pickMotivoPenalidad(row),
    totalFacturarSinIgv,
    igv: resolveIgv(row, totalFacturarSinIgv, totalFacturarConIgv),
    totalFacturarConIgv,
    obsProveedor: normalizeText(row.obsProveedor),
    obsGestor: normalizeText(row.obsGestor ?? row.obsBackoffice),
    estado: normalizeText(row.estado),
    estadoDescripcion:normalizeText(row.estadoDescripcion),
    esAdicional: normalizeBoolean(row.esAdicional),
    esManual: normalizeBoolean(row.esManual),
    seleccionado: normalizeBoolean(row.seleccionado),
  };
}

export function mapGestorCentralizadoResumen(resumen) {
  if (!isPlainObject(resumen)) {
    return getEmptyGestorCentralizadoResumen();
  }

  const totalFacturarSinIgv = normalizeNumber(
    resumen.totalFacturarSinIgv ?? resumen.totFacturarSinIgv,
  );
  const totalFacturarConIgv = normalizeNumber(
    resumen.totalFacturarConIgv ?? resumen.totFacturarConIgv,
  );

  return {
    costoTotalSinIgv: normalizeNumber(resumen.costoTotalSinIgv),
    costoTotalConIgv: normalizeNumber(resumen.costoTotalConIgv),
    penalidad: normalizeNumber(resumen.penalidad ?? resumen.totalPenalidad),
    totalFacturarSinIgv,
    igv:
      normalizeNumber(resumen.igv ?? resumen.totalIgv) ||
      resolveIgv(resumen, totalFacturarSinIgv, totalFacturarConIgv),
    totalFacturarConIgv,
    existenPendientes: Boolean(resumen.existenPendientes),
    existenObservadas: Boolean(resumen.existenObservadas),
    existenPendientesSinResolver: Boolean(resumen.existenPendientesSinResolver),
  };
}

const ITEM_ARRAY_KEYS = [
  "ordenes",
  "items",
  "registros",
  "lista",
  "rows",
  "datos",
  "results",
  "data",
];

function extractArrayFromObject(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  for (const key of ITEM_ARRAY_KEYS) {
    if (Array.isArray(value[key])) {
      return value[key];
    }
  }

  return null;
}

function normalizeItems(response) {
  if (Array.isArray(response)) return response;

  if (Array.isArray(response?.ordenes)) return response.ordenes;
  if (Array.isArray(response?.items)) return response.items;

  const nestedInData = extractArrayFromObject(response?.data);
  if (nestedInData) return nestedInData;

  const nestedInRoot = extractArrayFromObject(response);
  if (nestedInRoot) return nestedInRoot;

  return [];
}

function normalizeResumen(response) {
  if (isPlainObject(response?.resumen)) return response.resumen;
  if (isPlainObject(response?.summary)) return response.summary;
  return response?.resumen ?? {};
}

export function mapGestorCentralizadoDetalleEditable(row) {
  const base = mapGestorCentralizadoRow(row);

  return {
    idDetalle: row.idDetalle ?? row.idLiquidacionOs ?? null,
    idLiquidacionOs: row.idLiquidacionOs ?? null,
    orden: normalizeText(row.orden ?? "-"),
    skuServicio: base.skuServicio,
    servicio: base.servicio,
    skuProducto: base.skuProducto,
    producto: base.producto,
    cantidad: base.cantidad,

    costoTotalSinIgv: base.costoTotalSinIgv,
    costoTotalConIgv: base.costoTotalConIgv,
    costoSinIgv:      base.costoTotalSinIgv,
    costoConIgv:      base.costoTotalConIgv,

    totalFacturarSinIgv: base.totalFacturarSinIgv,  
    totalFacturarConIgv: base.totalFacturarConIgv,  

    igv: base.igv,

    obsProveedor: base.obsProveedor,
    _dirty: false,
  };
}

function mapPenalidadesFromRow(row = {}, penalidadAmount = 0, motivoPenalidad = "") {
  if (Array.isArray(row.penalidades) && row.penalidades.length) {
    return row.penalidades.map((entry) => ({
      idPenalidad: entry.idPenalidad ?? entry.id ?? null,
      tipoId: entry.tipoId ?? null,
      codigo: entry.codigo ?? null,
      descripcion: normalizeText(entry.descripcion ?? entry.motivo ?? motivoPenalidad ?? "Penalidad"),
      monto: normalizeNumber(entry.monto ?? penalidadAmount),
      porcentaje: normalizeNumber(entry.porcentaje),
      precioMercaderia: normalizeNumber(entry.precioMercaderia),
      flete: normalizeNumber(entry.flete),
    }));
  }

  if (penalidadAmount > 0 || motivoPenalidad) {
    return [
      {
        codigo: row.codigoPenalidad || null, 
        descripcion: motivoPenalidad || "Penalidad",
        monto: penalidadAmount,
        porcentaje: normalizeNumber(row.porcentajePenalidad ?? row.porcentaje),
      },
    ];
  }

  return [];
}

export function mapGestorCentralizadoEditableRow(row, adicionales = []) {
  const base = mapGestorCentralizadoRow(row);
  const costoSinIgv = base.costoTotalSinIgv;
  const costoConIgv = base.costoTotalConIgv;
  const totalSinIgv = base.totalFacturarSinIgv;
  const totalConIgv = base.totalFacturarConIgv;
  const penalidadAmount = base.penalidad;
  const motivoPenalidad = base.motivoPenalidad;

  return {
    ...base,
    costoSinIgv,
    costoConIgv,
    totalSinIgv,
    totalConIgv,
    penalidades: mapPenalidadesFromRow(row, penalidadAmount, motivoPenalidad),
    obsBackoffice: base.obsGestor,
    confirmadaGestor: false,
    detalles: adicionales.map(mapGestorCentralizadoDetalleEditable),
    _costoOriginal: costoSinIgv,
    _estadoOriginal: base.estado,
    _cantidadOriginal: base.cantidad,
    _obsBackofficeOriginal: base.obsGestor || null,
    _obsTmp: base.obsGestor || "",
    _dirty: false,
    _eliminarPenalidad: false,
    _showDetails: adicionales.length > 0,
  };
}

function groupGestorCentralizadoEditableItems(allOrdenes = []) {
  const parentRows = allOrdenes.filter(
    (row) => !normalizeBoolean(row.esAdicional),
  );

  return parentRows.map((row) => {
    const sequenceId = resolveParentSequenceId(row);
    const adicionalesFromFlat = allOrdenes.filter(
      (item) =>
        normalizeBoolean(item.esAdicional) &&
        resolveAdicionalParentId(item) === sequenceId,
    );
    const adicionales =
      adicionalesFromFlat.length > 0
        ? adicionalesFromFlat
        : extractNestedAdicionales(row);

    return mapGestorCentralizadoEditableRow(row, adicionales);
  });
}

export function mapGestorCentralizadoEditableResponse(
  response,
  pagination = {},
) {
  const allOrdenes = normalizeItems(response);
  const items = groupGestorCentralizadoEditableItems(allOrdenes);

  return {
    resumen: mapGestorCentralizadoResumen(normalizeResumen(response)),
    items,
    idLiquidacionCab: resolveIdLiquidacionCab(items, response),
    estadoLiquidacion: resolveEstadoLiquidacion(response, items),
    total: parseGestorCentralizadoTotal(response, items.length),
    page: Number(response?.page ?? pagination.page ?? 1),
    pageSize: Number(
      response?.pageSize ?? pagination.pageSize ?? pagination.perPage ?? 10,
    ),
    totalPages: Number(response?.totalPages ?? 0),
    raw: response,
  };
}

export function mapGestorCentralizadoResponse(response, pagination = {}) {
  const items = normalizeItems(response).map(mapGestorCentralizadoRow);

  return {
    resumen: mapGestorCentralizadoResumen(normalizeResumen(response)),
    items,
    total: parseGestorCentralizadoTotal(response, items.length),
    page: Number(response?.page ?? pagination.page ?? 1),
    pageSize: Number(
      response?.pageSize ?? pagination.pageSize ?? pagination.perPage ?? 10,
    ),
    totalPages: Number(response?.totalPages ?? 0),
    raw: response,
  };
}
